import type { MtbTrailMetadata, MtbTrailRelatedPlace } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Icon } from "~/components/ui/icons/icon";
import { Tag } from "~/components/ui/tag";
import { MtbDifficultyIcon } from "~/components/ui/icons/difficulty-icons";
import { CATEGORY_LABELS, CHARACTER_LABELS } from "./mtbTrailLabels";

interface TrailListItemProps {
  place: MtbTrailRelatedPlace;
  onRemove?: () => void;
  onSelect?: () => void;
  disabled?: boolean;
}

export function TrailListItem({ place, onRemove, onSelect, disabled }: TrailListItemProps) {
  const metadata = place.metadata;
  const interactive = !!onSelect;
  const Tag = interactive ? "button" : "div";

  return (
    <div className="group relative flex items-center">
      <Tag
        type={interactive ? "button" : undefined}
        onClick={onSelect}
        aria-label={interactive ? `View ${place.name}` : undefined}
        className={cn("flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-2 text-left", {
          "cursor-pointer transition-colors hover:bg-muted/50": interactive,
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring": interactive,
        })}
      >
        {metadata?.difficulty ? (
          <MtbDifficultyIcon
            difficulty={metadata.difficulty}
            aria-label={metadata.difficulty}
            className="size-5 shrink-0"
          />
        ) : (
          <div className="size-5 shrink-0" />
        )}
        <span className="flex-1 truncate text-sm font-medium">{place.name}</span>
        <TrailTags metadata={metadata} />
        {interactive && <Icon i="chevron-right" className="size-3.5 shrink-0 text-muted-foreground/50" />}
      </Tag>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${place.name}`}
          className={cn(
            "shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            { "cursor-not-allowed opacity-50": disabled },
          )}
        >
          <Icon i="delete-image" className="size-4" />
        </button>
      )}
    </div>
  );
}

function TrailTags({ metadata }: { metadata?: MtbTrailMetadata }) {
  if (!metadata) return null;

  const categoryLabel = metadata.category ? CATEGORY_LABELS[metadata.category] : undefined;
  const characterLabel = metadata.character?.[0] ? CHARACTER_LABELS[metadata.character[0]] : undefined;

  if (!categoryLabel && !characterLabel) return null;

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {categoryLabel && <Tag>{categoryLabel}</Tag>}
      {characterLabel && <Tag>{characterLabel}</Tag>}
    </div>
  );
}
